import {
  IPageRequest,
  IPagedResult,
  IResult,
  ISelectOption,
} from "src/Shared/Interfaces";
import { ICompanyList, ICompanyFilter, ICompany } from "../Interfaces";
import HttpClient from "src/Shared/Tools/HttpClient";
import { CompanyApiUrls } from "../Tools/CompanyApiUrls";
import { Observable } from "rxjs";
import { ICompanyOverview } from "../Interfaces/ICompanyOverview";
import { BaseService } from "src/Shared/Services/Base.service";
import { ICompanySearchFilter } from "../Interfaces/ICompanySearchFilter";
import { IArchivedCompany } from "../Interfaces/IArchivedCompany";
import { ICompanySLAInfoDTO } from "../Interfaces/ICompanySLAInfoDTO";
import { ISlaReporting } from "../Interfaces/ISlaReporting";
import { ICompaniesSLAFilterRequest } from "../Interfaces/ICompaniesSLAFilterRequest";
import { ILinkImperansCompany } from "../Interfaces/ILinkImperansCompany";
import { ICompanyImperansIntegration } from "../Interfaces/ICompanyImperansIntegration";
import { IMenuGroupResponse } from "../Interfaces/IMenuGroupResponse";
import { ICompanyActivity, IActivityPagedResponse } from "../Interfaces/ICompanyActivity";
import { IUpdateSLAInfoRequest } from "../Interfaces/IUpdateSLAInfoRequest";
import { ISLAInfo } from "../Interfaces/ISLAInfo";
import { IProspectList } from "../Interfaces/IProspectList";
import { IOnboardingProgress } from "../Interfaces/IOnboardingProgress";
import { IOnboardingRecord } from "../Interfaces/IOnboardingRecord";
import {
  IOnboardingDraftRequest,
  IOnboardingWizardState,
} from "../Interfaces/IOnboardingWizardState";
import {
  IOnboardingExport,
  IOnboardingImportIssue,
  IOnboardingImportRequest,
  IOnboardingSopDocument,
  IOnboardingSubmissionPlan,
} from "../Interfaces/IOnboardingOutputs";
import { IUpdateClientDetails } from "../Interfaces/IUpdateClientDetails";
import { IImperansSubmission } from "../Interfaces/IImperansSubmission";

/** A generated or checked company code (S1.3). */
export interface ICompanyCodeResult {
  companyCode: string;
  isAvailable: boolean;
  message: string | null;
}

class CompanyService extends BaseService {
  public getCompanies(
    request: IPageRequest<ICompanyFilter>,
  ): Observable<IPagedResult<ICompanyList>> {
    const http: HttpClient = new HttpClient();
    return http.post<IPagedResult<ICompanyList>>(
      CompanyApiUrls.GetCompanies,
      request,
    );
  }

  public addCompany(request: ICompany) {
    const http: HttpClient = new HttpClient();
    return http.post<IResult<string>>(CompanyApiUrls.AddCompany, request);
  }

  public updateCompany(request: ICompany) {
    const http: HttpClient = new HttpClient();
    return http.post<IResult<string>>(CompanyApiUrls.UpdateCompany, request);
  }

  public getCompany(companyRowID: string) {
    const http: HttpClient = new HttpClient();
    return http.get<ICompany>(CompanyApiUrls.GetCompany(companyRowID));
  }

  public getArchivedCompany(archivedCRMDataID: number) {
    const http: HttpClient = new HttpClient();
    return http.get<IArchivedCompany>(
      CompanyApiUrls.GetArchivedCompany +
        `?archivedCRMDataID=${archivedCRMDataID}`,
    );
  }

  public getCompanyOverview(companyRowID: string) {
    const http: HttpClient = new HttpClient();
    return http.get<ICompanyOverview>(
      CompanyApiUrls.GetCompanyOverview + `?companyRowID=${companyRowID}`,
    );
  }

  public getCompanySearch(companyName: string, totalCount: number) {
    return this.http.get<ICompanySearchFilter[]>(
      CompanyApiUrls.Search(companyName, totalCount),
    );
  }

  public getCompanySLAInfo(companyRowID: string) {
    return this.http.get<ICompanySLAInfoDTO>(
      CompanyApiUrls.CompanySLAInfo + `?companyRowID=${companyRowID}`,
    );
  }

  public addOrUpdateCompanySLAInfo(request: ICompanySLAInfoDTO) {
    return this.http.post<IResult<string>>(
      CompanyApiUrls.CompanySLAInfo,
      request,
    );
  }

  public slaReporting(request: IPageRequest<ICompaniesSLAFilterRequest>) {
    return this.http.post<IPagedResult<ISlaReporting>>(
      CompanyApiUrls.SlaReporting,
      request,
    );
  }

  public linkCompanyToImperans(request: ILinkImperansCompany) {
    return this.http.put<IResult<string>>(CompanyApiUrls.Link, request);
  }

  public getCompanyImperansIntegration(companyRowID: string) {
    return this.http.get<ICompanyImperansIntegration>(
      CompanyApiUrls.Link + `?companyRowID=${companyRowID}`,
    );
  }

  public getCompanyMenu(companyRowID: string | null = null) {
    return this.http.get<IMenuGroupResponse[]>(
      CompanyApiUrls.Menu +
        (companyRowID ? `?companyRowID=${companyRowID}` : ""),
    );
  }

  public getCompanyActivity(companyRowID: string, pageNumber: number = 1, pageSize: number = 10) {
    return this.http.get<IActivityPagedResponse>(
      CompanyApiUrls.Activity + `?companyRowID=${companyRowID}&pageNumber=${pageNumber}&pageSize=${pageSize}`,
    );
  }

  public getProspectsCount() {
    return this.http.get<number>(CompanyApiUrls.ProspectsCount);
  }

  public getProspects() {
    return this.http.get<IProspectList[]>(CompanyApiUrls.Prospects);
  }

  public getOnboardingProgress(companyRowID: string) {
    return this.http.get<IOnboardingProgress>(
      CompanyApiUrls.OnboardingProgress + `?companyRowID=${companyRowID}`,
    );
  }

  /**
   * Submit sends the onboarding to the paired Imperans and waits for its answer (Epic 6303,
   * Feature CRM1); the answer comes back as the result's content (story 6339).
   */
  public submitOnboarding(companyRowID: string, captureConfirmed: boolean) {
    return this.http.post<IResult<IImperansSubmission | IOnboardingImportIssue[]>>(
      CompanyApiUrls.SubmitOnboarding,
      {
        companyRowID,
        captureConfirmed,
      },
    );
  }

  /** Try again with the same Idempotency-Key after a timeout, fault or refusal (story 6338). */
  public retryImperansSubmission(companyRowID: string) {
    return this.http.post<IResult<IImperansSubmission>>(CompanyApiUrls.RetryImperansSubmission, {
      companyRowID,
    });
  }

  /** Where the latest submission stands with Imperans; null when nothing was ever sent. */
  public getImperansSubmission(companyRowID: string) {
    return this.http.get<IImperansSubmission | null>(
      CompanyApiUrls.ImperansSubmission + `?companyRowID=${companyRowID}`,
    );
  }

  /** Generate a code from the client's stored name and country (S1.3); does not save. */
  public generateCompanyCode(companyRowID: string) {
    return this.http.get<ICompanyCodeResult>(
      CompanyApiUrls.GenerateCompanyCode + `?companyRowID=${companyRowID}`,
    );
  }

  /** Check a typed code against the CRM and Imperans, with their distinct messages (S1.3). */
  public checkCompanyCodeAvailability(companyRowID: string, companyCode: string) {
    return this.http.get<ICompanyCodeResult>(
      CompanyApiUrls.CompanyCodeAvailability +
        `?companyRowID=${companyRowID}&companyCode=${encodeURIComponent(companyCode)}`,
    );
  }

  /** The SOP as data: eleven blocks in fixed order, draft-marked before submission (S7.8). */
  public getOnboardingSopDocument(companyRowID: string) {
    return this.http.get<IOnboardingSopDocument>(
      CompanyApiUrls.OnboardingSopDocument + `?companyRowID=${companyRowID}`,
    );
  }

  /** The versioned JSON envelope and its deterministic filename (S7.14). */
  /** The schemaVersion 2 Imperans import request and its self-check (Epic 6303 CRM2); read-only. */
  public getOnboardingImportRequest(companyRowID: string, captureConfirmed = false) {
    return this.http.get<IOnboardingImportRequest>(
      CompanyApiUrls.OnboardingImportRequest + `?companyRowID=${companyRowID}&captureConfirmed=${captureConfirmed}`,
    );
  }

  public getOnboardingExport(companyRowID: string) {
    return this.http.get<IOnboardingExport>(
      CompanyApiUrls.OnboardingExport + `?companyRowID=${companyRowID}`,
    );
  }

  /** What submission will do, for the shared cross-system modal (S7.7). */
  public getOnboardingSubmissionPlan(companyRowID: string) {
    return this.http.get<IOnboardingSubmissionPlan>(
      CompanyApiUrls.OnboardingSubmissionPlan + `?companyRowID=${companyRowID}`,
    );
  }

  public getOnboardingWizardState(companyRowID: string) {
    return this.http.get<IOnboardingWizardState>(
      CompanyApiUrls.OnboardingWizardState + `?companyRowID=${companyRowID}`,
    );
  }

  /** Write-through save of the onboarding payload (story 5589). */
  public saveOnboardingDraft(request: IOnboardingDraftRequest) {
    return this.http.post<IResult<string>>(
      CompanyApiUrls.OnboardingDraft,
      request,
    );
  }

  /** Request (complete = false) or complete the specialist review (story 5591). */
  public saveOnboardingReview(
    companyRowID: string,
    complete: boolean,
    notes?: {
      spokeTo?: string;
      phoneNumber?: string;
      emailAddress?: string;
      notes?: string;
    },
  ) {
    return this.http.post<IResult<string>>(CompanyApiUrls.OnboardingReview, {
      companyRowID,
      complete,
      notes,
    });
  }

  /** Terms acknowledgement and marketing opt-in, recorded independently (story 5595). */
  public saveOnboardingAcknowledgements(request: {
    companyRowID: string;
    termsAcknowledged?: boolean;
    marketingOptIn?: boolean;
  }) {
    return this.http.post<IResult<string>>(
      CompanyApiUrls.OnboardingAcknowledgements,
      request,
    );
  }

  public getOnboardingRecord(companyRowID: string) {
    return this.http.get<IOnboardingRecord>(
      CompanyApiUrls.OnboardingRecord + `?companyRowID=${companyRowID}`,
    );
  }

  public updateClientDetails(request: IUpdateClientDetails) {
    return this.http.post<IResult<string>>(
      CompanyApiUrls.UpdateClientDetails,
      request,
    );
  }

  public setImperansLinkFlag(companyRowID: string, isLinked: boolean) {
    return this.http.put<IResult<string>>(CompanyApiUrls.LinkFlag, {
      companyRowID,
      isLinked,
    });
  }

  public deleteCompany(companyRowID: string) {
    return this.http.post<IResult<string>>(CompanyApiUrls.DeleteCompany, {
      companyRowID,
    });
  }

  public promoteToOnboarding(companyRowID: string) {
    return this.http.put<IResult<string>>(CompanyApiUrls.PromoteToOnboarding, {
      companyRowID,
    });
  }

  public markAsCurrentClient(companyRowID: string) {
    return this.http.put<IResult<string>>(CompanyApiUrls.MarkAsCurrentClient, {
      companyRowID,
    });
  }

  public savePriceReviewComment(request: any) {
    return this.http.post<string>(
      CompanyApiUrls.SavePriceReviewComment,
      request,
    );
  }

  public getCompanyRelationshipManager(companyRowID: string) {
    return this.http.get<ISelectOption[]>(
      CompanyApiUrls.RelationshipManagers + `?companyRowID=${companyRowID}`,
    );
  }

  public updateSLAInfo(request: FormData) {
    return this.http.post<string>(CompanyApiUrls.UpdateSLAInfo, request);
  }
  public getSLAInfo(companyRowID: string) {
    return this.http.get<ISLAInfo>(
      CompanyApiUrls.GetSLAInfo + `?companyRowID=${companyRowID}`,
    );
  }
}
export default new CompanyService();
